import { useCallback, useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import { fetchWorkAreas, type WorkAreaOption } from 'modules/admin/api/plotPlanService'
import { searchRoles } from 'modules/admin/api/roleService'
import { searchUserRoleAssignments } from 'modules/admin/api/userRoleAssignmentService'
import {
  cancelPermit,
  cancelPermitsBulk,
  getCancelReasons,
  getOpenPermits,
  openPermit,
  openPermitsBulk,
  type CancelPermitBulkRequestPayload,
  type CancelPermitRequestPayload,
  type CancelReasonOption,
  type OpenPermitBulkRequestPayload,
  type OpenPermitListItem,
  type OpenPermitRequestPayload,
  type PermitEmployeeLookup,
} from 'modules/permit/api/permitService'
import { createDefaultFilters, DEFAULT_TENANT_ID, PAGE_SIZE } from './constants'
import type { FilterState, LayoutState, OpenPermitStats, SupervisorSelection } from './types'
import {
  canOpenPermitToday,
  getErrorMessage,
  normalizeGuid,
  resolveMenuRoleId,
  resolveAccessibleLocationIds,
} from './utils'
import { requestNavCountsRefresh } from 'modules/permit/utils/navCountRefresh'
import { getPersistedSelectedSiteId, resolveTenantCandidate, resolveTenantId } from 'shared/utils/tenantContext'

const OPEN_PERMIT_OPERATOR_ROLE_ID = (process.env.REACT_APP_OPEN_PERMIT_OPERATOR_ROLE_ID ?? '').trim()
const OPEN_PERMIT_OPERATOR_ROLE_CODE = (process.env.REACT_APP_OPEN_PERMIT_OPERATOR_ROLE_CODE ?? '30031').trim()
const ALLOWED_OPEN_PERMIT_TYPES = ['Hot Work', 'Cold Work', 'Confine Work Space'] as const
const COMBINED_OPEN_FETCH_LIMIT = 500

const getPermitSortTime = (permit: OpenPermitListItem) => {
  const timestamp = Date.parse(
    permit.plannedStart ??
    permit.plannedEnd ??
    permit.approvedAt ??
    '',
  )
  return Number.isNaN(timestamp) ? 0 : timestamp
}

export function useOpenPermitPage() {
  const location = useLocation()
  const navigate = useNavigate()
  const state = useMemo(() => ((location.state as LayoutState | undefined) ?? {}), [location.state])
  const persistedSiteId = useMemo(() => getPersistedSelectedSiteId(), [])
  const tenantId = resolveTenantId(state.site?.id, persistedSiteId, DEFAULT_TENANT_ID)
  const siteId = resolveTenantCandidate(state.site?.id, persistedSiteId)

  const [filters, setFilters] = useState<FilterState>(createDefaultFilters)
  const [appliedFilters, setAppliedFilters] = useState<FilterState>(createDefaultFilters)
  const [permits, setPermits] = useState<OpenPermitListItem[]>([])
  const [workAreaOptions, setWorkAreaOptions] = useState<WorkAreaOption[]>([])
  const [cancelReasons, setCancelReasons] = useState<CancelReasonOption[]>([])
  const [selectedPermitIds, setSelectedPermitIds] = useState<string[]>([])
  const [openDialogPermitIds, setOpenDialogPermitIds] = useState<string[]>([])
  const [cancelDialogPermitIds, setCancelDialogPermitIds] = useState<string[]>([])
  const [isOpenDialogOpen, setIsOpenDialogOpen] = useState(false)
  const [isCancelDialogOpen, setIsCancelDialogOpen] = useState(false)
  const [isSubmittingOpen, setIsSubmittingOpen] = useState(false)
  const [isSubmittingCancel, setIsSubmittingCancel] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [pageNumber, setPageNumber] = useState(1)
  const [totalItems, setTotalItems] = useState(0)
  const [statistics, setStatistics] = useState<OpenPermitStats>({
    total: 0,
    coldWork: 0,
    hotWork: 0,
    confineWork: 0,
  })
  const [employeeSearchTerm, setEmployeeSearchTerm] = useState('')
  const [employeeResults, setEmployeeResults] = useState<PermitEmployeeLookup[]>([])
  const [isEmployeeSearchLoading, setIsEmployeeSearchLoading] = useState(false)
  const [openPermitOperatorRoleId, setOpenPermitOperatorRoleId] = useState<string | null>(normalizeGuid(OPEN_PERMIT_OPERATOR_ROLE_ID))
  const [supervisors, setSupervisors] = useState<SupervisorSelection[]>([])
  const [cancelReason, setCancelReason] = useState('')
  const [cancelReasonDetail, setCancelReasonDetail] = useState('')

  const currentUserId = useMemo(() => {
    const candidates = [
      state.user?.id,
      state.user?.user_id,
      state.user?.userId,
      state.user?.employee_id,
      state.user?.employeeId,
      state.user?.emp_id,
      state.user?.empId,
    ]
    for (const candidate of candidates) {
      const normalized = normalizeGuid(candidate)
      if (normalized) return normalized
    }
    return null
  }, [state.user])

  const activeRoleId = useMemo(
    () => resolveMenuRoleId(state.menus, '/permit-open'),
    [state.menus],
  )

  const accessibleLocationIds = useMemo(
    () => resolveAccessibleLocationIds(state.user, state.roles, activeRoleId),
    [activeRoleId, state.roles, state.user],
  )
  const selectedLocationIds = useMemo(() => {
    if (appliedFilters.workAreaId && appliedFilters.workAreaId !== 'all') return [appliedFilters.workAreaId]
    return undefined
  }, [appliedFilters.workAreaId])

  useEffect(() => {
    if (!state.user) navigate('/modules', { replace: true })
  }, [navigate, state.user])

  useEffect(() => {
    const configuredRoleId = normalizeGuid(OPEN_PERMIT_OPERATOR_ROLE_ID)
    if (configuredRoleId) {
      setOpenPermitOperatorRoleId(configuredRoleId)
      return
    }

    let cancelled = false
    searchRoles({ isActive: true, page: 1, pageSize: 200 })
      .then((response) => {
        if (cancelled) return
        const matchedRole = (response.items ?? []).find((role) => role.code?.trim() === OPEN_PERMIT_OPERATOR_ROLE_CODE)
        setOpenPermitOperatorRoleId(normalizeGuid(matchedRole?.roleId) ?? null)
      })
      .catch((error) => {
        console.error('PermitOpenPage: failed to resolve operator role', error)
        if (!cancelled) setOpenPermitOperatorRoleId(null)
      })

    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    let cancelled = false
    fetchWorkAreas(tenantId)
      .then((items) => {
        if (cancelled) return
        const sourceItems = items ?? []
        const nextItems = accessibleLocationIds.length > 0
          ? sourceItems.filter((item) => accessibleLocationIds.includes(item.locationId.toLowerCase()))
          : []
        setWorkAreaOptions(nextItems)
      })
      .catch((error) => {
        console.error('PermitOpenPage: failed to load work areas', error)
        if (!cancelled) setWorkAreaOptions([])
      })
    return () => {
      cancelled = true
    }
  }, [accessibleLocationIds, tenantId])

  useEffect(() => {
    let cancelled = false
    getCancelReasons(tenantId)
      .then((items) => {
        if (!cancelled) setCancelReasons(items ?? [])
      })
      .catch((error) => {
        console.error('PermitOpenPage: failed to load cancel reasons', error)
        if (!cancelled) setCancelReasons([])
      })
    return () => {
      cancelled = true
    }
  }, [tenantId])

  const applyFilters = useCallback((nextFilters: FilterState) => {
    if (pageNumber !== 1) setPageNumber(1)
    setAppliedFilters({ ...nextFilters })
  }, [pageNumber])

  const loadPermits = useCallback(async () => {
    setIsLoading(true)
    setLoadError(null)
    try {
      const baseQuery = {
        tenantId,
        activeRoleId: activeRoleId ?? undefined,
        siteId,
        locationIds: selectedLocationIds,
        searchTerm: appliedFilters.search.trim() || undefined,
        plannedStartFrom: appliedFilters.workDate || undefined,
        plannedStartTo: appliedFilters.workDate || undefined,
      }

      if (appliedFilters.permitType === 'all') {
        const responses = await Promise.all(
          ALLOWED_OPEN_PERMIT_TYPES.map((permitType) =>
            getOpenPermits({
              ...baseQuery,
              permitType,
              page: 1,
              pageSize: COMBINED_OPEN_FETCH_LIMIT,
            }),
          ),
        )

        const mergedItems = responses
          .flatMap((response) => response.items ?? [])
          .filter((permit, index, items) => items.findIndex((item) => item.permitId === permit.permitId) === index)
          .sort((a, b) => getPermitSortTime(b) - getPermitSortTime(a))

        const [hotResponse, coldResponse, confineResponse] = responses
        const hotWork = hotResponse?.totalItems ?? hotResponse?.items?.length ?? 0
        const coldWork = coldResponse?.totalItems ?? coldResponse?.items?.length ?? 0
        const confineWork = confineResponse?.totalItems ?? confineResponse?.items?.length ?? 0
        const combinedTotal = hotWork + coldWork + confineWork

        const startIndex = (pageNumber - 1) * PAGE_SIZE
        const endIndex = startIndex + PAGE_SIZE
        setPermits(mergedItems.slice(startIndex, endIndex))
        setTotalItems(combinedTotal)
        setStatistics({
          total: combinedTotal,
          coldWork,
          hotWork,
          confineWork,
        })
      } else {
        const response = await getOpenPermits({
          ...baseQuery,
          permitType: appliedFilters.permitType,
          page: pageNumber,
          pageSize: PAGE_SIZE,
        })
        const responseTotal = response.totalItems ?? 0
        setPermits(response.items ?? [])
        setTotalItems(responseTotal)
        setStatistics({
          total: responseTotal,
          coldWork: appliedFilters.permitType === 'Cold Work' ? responseTotal : 0,
          hotWork: appliedFilters.permitType === 'Hot Work' ? responseTotal : 0,
          confineWork: appliedFilters.permitType === 'Confine Work Space' ? responseTotal : 0,
        })
      }
    } catch (error) {
      console.error('PermitOpenPage: failed to load permits', error)
      setPermits([])
      setTotalItems(0)
      setStatistics({
        total: 0,
        coldWork: 0,
        hotWork: 0,
        confineWork: 0,
      })
      setLoadError(getErrorMessage(error, 'à¹„à¸¡à¹ˆà¸ªà¸²à¸¡à¸²à¸£à¸–à¹‚à¸«à¸¥à¸”à¸£à¸²à¸¢à¸à¸²à¸£ Permit à¹„à¸”à¹‰'))
    } finally {
      setIsLoading(false)
    }
  }, [activeRoleId, appliedFilters.permitType, appliedFilters.search, appliedFilters.workDate, pageNumber, selectedLocationIds, siteId, tenantId])

  useEffect(() => {
    loadPermits()
  }, [loadPermits])

  const selectablePermits = useMemo(() => permits.filter((permit) => canOpenPermitToday(permit)), [permits])

  useEffect(() => {
    setSelectedPermitIds((prev) => prev.filter((id) => selectablePermits.some((permit) => permit.permitId === id)))
  }, [selectablePermits])

  useEffect(() => {
    if (!isOpenDialogOpen) return
    let cancelled = false
    const timer = setTimeout(async () => {
      const term = employeeSearchTerm.trim()
      if (term.length < 2 || !openPermitOperatorRoleId) {
        setEmployeeResults([])
        return
      }
      setIsEmployeeSearchLoading(true)
      try {
        const result = await searchUserRoleAssignments({
          tenantId,
          roleId: openPermitOperatorRoleId,
          search: term,
          isActive: true,
          page: 1,
          pageSize: 10,
        })
        if (!cancelled) {
          const dedupedResults = new Map<string, PermitEmployeeLookup>()
          for (const item of result.items ?? []) {
            const employeeId = normalizeGuid(item.employeeId)
            if (!employeeId || dedupedResults.has(employeeId)) continue
            dedupedResults.set(employeeId, {
              employeeId,
              userId: normalizeGuid(item.userId),
              employeeName: item.fullName?.trim() || item.username?.trim() || item.email?.trim() || employeeId,
              position: item.roleName ?? null,
              department: item.departmentName ?? null,
              email: item.email ?? null,
              phone: null,
            })
          }
          setEmployeeResults(Array.from(dedupedResults.values()))
        }
      } catch (error) {
        if (!cancelled) {
          console.error('PermitOpenPage: employee search failed', error)
          toast.error('à¹„à¸¡à¹ˆà¸ªà¸²à¸¡à¸²à¸£à¸–à¸„à¹‰à¸™à¸«à¸²à¸œà¸¹à¹‰à¸›à¸à¸´à¸šà¸±à¸•à¸´à¸‡à¸²à¸™à¹„à¸”à¹‰')
        }
      } finally {
        if (!cancelled) setIsEmployeeSearchLoading(false)
      }
    }, 300)

    return () => {
      cancelled = true
      clearTimeout(timer)
    }
  }, [employeeSearchTerm, isOpenDialogOpen, openPermitOperatorRoleId, tenantId])

  const totalPages = Math.max(Math.ceil(totalItems / PAGE_SIZE), 1)
  const isAllSelected = selectablePermits.length > 0 && selectedPermitIds.length === selectablePermits.length
  const openDialogPermits = useMemo(() => permits.filter((permit) => openDialogPermitIds.includes(permit.permitId)), [openDialogPermitIds, permits])
  const cancelDialogPermits = useMemo(() => permits.filter((permit) => cancelDialogPermitIds.includes(permit.permitId)), [cancelDialogPermitIds, permits])

  const handleToggleSelectAll = useCallback((checked: boolean) => {
    setSelectedPermitIds(checked ? selectablePermits.map((permit) => permit.permitId) : [])
  }, [selectablePermits])

  const togglePermitSelection = useCallback((permitId: string, checked: boolean) => {
    const permit = selectablePermits.find((item) => item.permitId === permitId)
    if (!permit) return
    setSelectedPermitIds((prev) => checked ? (prev.includes(permitId) ? prev : [...prev, permitId]) : prev.filter((id) => id !== permitId))
  }, [selectablePermits])

  const openOpenDialog = useCallback((permitIds: string[]) => {
    if (permitIds.length === 0) return
    if (!currentUserId) {
      toast.error('à¹„à¸¡à¹ˆà¸žà¸šà¸£à¸«à¸±à¸ªà¸œà¸¹à¹‰à¹ƒà¸Šà¹‰à¸‡à¸²à¸™à¸ªà¸³à¸«à¸£à¸±à¸šà¸šà¸±à¸™à¸—à¸¶à¸à¸œà¸¹à¹‰à¹€à¸›à¸´à¸” Permit')
      return
    }
    const blockedPermits = permits.filter((permit) => permitIds.includes(permit.permitId) && !canOpenPermitToday(permit))
    if (blockedPermits.length > 0) {
      toast.error('à¹„à¸¡à¹ˆà¸ªà¸²à¸¡à¸²à¸£à¸–à¹€à¸›à¸´à¸” Permit à¸¥à¹ˆà¸§à¸‡à¸«à¸™à¹‰à¸²à¸ˆà¸²à¸à¸§à¸±à¸™à¸—à¸µà¹ˆà¹à¸ˆà¹‰à¸‡à¸‚à¸­à¸¡à¸²à¹„à¸”à¹‰')
      return
    }
    setOpenDialogPermitIds(permitIds)
    setSupervisors([])
    setEmployeeSearchTerm('')
    setEmployeeResults([])
    setIsOpenDialogOpen(true)
  }, [currentUserId, permits])

  const openCancelDialog = useCallback((permitIds: string[]) => {
    if (permitIds.length === 0) return
    setCancelDialogPermitIds(permitIds)
    setCancelReason('')
    setCancelReasonDetail('')
    setIsCancelDialogOpen(true)
  }, [])

  const handleAddSupervisor = useCallback((employee: PermitEmployeeLookup) => {
    setSupervisors((prev) => {
      if (prev.some((item) => item.employeeId === employee.employeeId)) return prev
      return [...prev, {
        employeeId: employee.employeeId,
        employeeName: employee.employeeName,
        position: employee.position,
        department: employee.department,
      }]
    })
  }, [])

  const handleRemoveSupervisor = useCallback((index: number) => {
    setSupervisors((prev) => prev.filter((_, idx) => idx !== index))
  }, [])

  const handleViewPermit = useCallback((permit: OpenPermitListItem) => {
    const params = new URLSearchParams({
      permitId: permit.permitId,
      mode: 'edit',
      entryMode: 'display',
      forceReadOnly: 'true',
      hideActionBar: 'true',
      tenantId,
    })
    window.open(`/permit-request?${params.toString()}`, '_blank', 'noopener')
  }, [tenantId])

  const handleSubmitOpen = useCallback(async () => {
    if (openDialogPermitIds.length === 0) return
    if (!currentUserId) {
      toast.error('à¹„à¸¡à¹ˆà¸žà¸šà¸£à¸«à¸±à¸ªà¸œà¸¹à¹‰à¹ƒà¸Šà¹‰à¸‡à¸²à¸™à¸ªà¸³à¸«à¸£à¸±à¸šà¸šà¸±à¸™à¸—à¸¶à¸à¸œà¸¹à¹‰à¹€à¸›à¸´à¸” Permit')
      return
    }
    if (supervisors.length === 0) {
      toast.error('à¸à¸£à¸¸à¸“à¸²à¸£à¸°à¸šà¸¸à¸œà¸¹à¹‰à¸›à¸à¸´à¸šà¸±à¸•à¸´à¸‡à¸²à¸™à¸­à¸¢à¹ˆà¸²à¸‡à¸™à¹‰à¸­à¸¢ 1 à¸„à¸™')
      return
    }

    setIsSubmittingOpen(true)
    try {
      const supervisorPayload = supervisors.map((item) => ({ employeeId: item.employeeId }))
      if (openDialogPermitIds.length === 1) {
        const payload: OpenPermitRequestPayload = {
          tenantId,
          permitId: openDialogPermitIds[0],
          activeRoleId: activeRoleId ?? undefined,
          openedByUserId: currentUserId,
          supervisors: supervisorPayload,
        }
        await openPermit(openDialogPermitIds[0], payload)
      } else {
        const payload: OpenPermitBulkRequestPayload = {
          tenantId,
          permitIds: openDialogPermitIds,
          activeRoleId: activeRoleId ?? undefined,
          openedByUserId: currentUserId,
          supervisors: supervisorPayload,
        }
        await openPermitsBulk(payload)
      }
      toast.success(`à¹€à¸›à¸´à¸” Permit à¸ªà¸³à¹€à¸£à¹‡à¸ˆ ${openDialogPermitIds.length} à¸£à¸²à¸¢à¸à¸²à¸£`)
      setSelectedPermitIds((prev) => prev.filter((id) => !openDialogPermitIds.includes(id)))
      setOpenDialogPermitIds([])
      setSupervisors([])
      setIsOpenDialogOpen(false)
      requestNavCountsRefresh()
      await loadPermits()
    } catch (error) {
      console.error('PermitOpenPage: open permit failed', error)
      toast.error(getErrorMessage(error, 'à¹„à¸¡à¹ˆà¸ªà¸²à¸¡à¸²à¸£à¸–à¹€à¸›à¸´à¸” Permit à¹„à¸”à¹‰'))
    } finally {
      setIsSubmittingOpen(false)
    }
  }, [activeRoleId, currentUserId, loadPermits, openDialogPermitIds, supervisors, tenantId])

  const handleSubmitCancel = useCallback(async () => {
    if (cancelDialogPermitIds.length === 0) return
    if (!cancelReason) {
      toast.error('à¸à¸£à¸¸à¸“à¸²à¹€à¸¥à¸·à¸­à¸à¹€à¸«à¸•à¸¸à¸œà¸¥à¹ƒà¸™à¸à¸²à¸£à¸¢à¸à¹€à¸¥à¸´à¸')
      return
    }

    setIsSubmittingCancel(true)
    try {
      if (cancelDialogPermitIds.length === 1) {
        const payload: CancelPermitRequestPayload = { tenantId, permitId: cancelDialogPermitIds[0], cancelledBy: currentUserId, reasonCode: cancelReason, reasonDetail: cancelReasonDetail || null }
        await cancelPermit(cancelDialogPermitIds[0], payload)
      } else {
        const payload: CancelPermitBulkRequestPayload = { tenantId, permitIds: cancelDialogPermitIds, cancelledBy: currentUserId, reasonCode: cancelReason, reasonDetail: cancelReasonDetail || null }
        await cancelPermitsBulk(payload)
      }
      toast.success(`à¸¢à¸à¹€à¸¥à¸´à¸ Permit à¸ªà¸³à¹€à¸£à¹‡à¸ˆ ${cancelDialogPermitIds.length} à¸£à¸²à¸¢à¸à¸²à¸£`)
      setSelectedPermitIds((prev) => prev.filter((id) => !cancelDialogPermitIds.includes(id)))
      setCancelDialogPermitIds([])
      setIsCancelDialogOpen(false)
      requestNavCountsRefresh()
      await loadPermits()
    } catch (error) {
      console.error('PermitOpenPage: cancel permit failed', error)
      toast.error(getErrorMessage(error, 'à¹„à¸¡à¹ˆà¸ªà¸²à¸¡à¸²à¸£à¸–à¸¢à¸à¹€à¸¥à¸´à¸ Permit à¹„à¸”à¹‰'))
    } finally {
      setIsSubmittingCancel(false)
    }
  }, [cancelDialogPermitIds, cancelReason, cancelReasonDetail, currentUserId, loadPermits, tenantId])

  const handleSearch = useCallback(() => {
    applyFilters(filters)
  }, [applyFilters, filters])

  const resetFilters = useCallback(() => {
    const nextFilters = {
      ...createDefaultFilters(),
      workDate: '',
    }
    setFilters(nextFilters)
    setAppliedFilters(nextFilters)
    setPageNumber(1)
  }, [])

  return {
    state,
    navigate,
    filters,
    setFilters,
    permits,
    workAreaOptions,
    cancelReasons,
    selectedPermitIds,
    openDialogPermitIds,
    cancelDialogPermitIds,
    isOpenDialogOpen,
    setIsOpenDialogOpen,
    isCancelDialogOpen,
    setIsCancelDialogOpen,
    isSubmittingOpen,
    isSubmittingCancel,
    isLoading,
    loadError,
    pageNumber,
    setPageNumber,
    totalItems,
    employeeSearchTerm,
    setEmployeeSearchTerm,
    employeeResults,
    isEmployeeSearchLoading,
    supervisors,
    cancelReason,
    setCancelReason,
    cancelReasonDetail,
    setCancelReasonDetail,
    statistics,
    totalPages,
    isAllSelected,
    selectableCount: selectablePermits.length,
    openDialogPermits,
    cancelDialogPermits,
    handleToggleSelectAll,
    togglePermitSelection,
    openOpenDialog,
    openCancelDialog,
    handleAddSupervisor,
    handleRemoveSupervisor,
    handleViewPermit,
    handleSubmitOpen,
    handleSubmitCancel,
    handleSearch,
    resetFilters,
  }
}
